// SHADOW MARKET™ — 3D Customization Studio (real GLB models)
// Models served from /public/models/:
//   /models/basic_t-shirt.glb         → camiseta
//   /models/collar_t-shirt_model.glb  → polo
//
// Features:
// - Load GLB and traverse meshes to swap base color in real-time
// - Decal overlay plane on the chest area driven by a 512×512 <canvas>
//   that mixes uploaded image + custom text. Plane is a child of the
//   model so it rotates with it.
// - Inertia drag rotation + auto-rotate + zoom (wheel)
// - Front / Side / Back / Auto view shortcuts
// - Side panel: garment color · size · tabs (DISEÑO / TEXTO / AJUSTE)
// - Hooks into useCart() to push customization payload (+€15)
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n } from "../i18n/shop";
import FontSelector from "./studio/FontSelector";
import { getFontPxFor } from "../data/tipografias-studio";
import { REBEL_DESIGNS } from "../data/rebel-designs";
import { getStudioDesignsI18n } from "../i18n/studio-designs";
import { CustomizerAccordion } from "./shop/CustomizerAccordion";

// Ensure a Google Font has been parsed by the browser before drawing it on
// a 2D canvas. Without this the FIRST paint may fall back to a system font
// even though the @font-face is requested in index.html.
const ensureFontLoaded = async (familia) => {
  try {
    if (document.fonts && document.fonts.load) {
      await document.fonts.load(`bold 16px "${familia}"`);
    }
  } catch (_e) {
    /* fall back to system font silently */
  }
};

// Garment colors — deep values so black reads black with low ambient
const COLORS = [
  { name: "VOID BLACK",    hex: "#080808", three: 0x080808 },
  { name: "CONCRETE GREY", hex: "#555555", three: 0x555555 },
  { name: "CREAM",         hex: "#bab5a0", three: 0xbab5a0 },
  { name: "DEEP NAVY",     hex: "#0a0a2a", three: 0x0a0a2a },
  { name: "BLOOD RED",     hex: "#3a0808", three: 0x3a0808 },
  { name: "FOREST",        hex: "#0a1a0a", three: 0x0a1a0a },
];

const MODEL_PATH = {
  camiseta: "/models/basic_t-shirt.glb",
  polo: "/models/collar_t-shirt_model.glb",
};

// Module-level cache: load each GLB exactly once per session as an
// ArrayBuffer via fetch(). We then parse with GLTFLoader.parse() at
// usage time. This bypasses GLTFLoader.load()'s internal fetch which
// behaves unpredictably under React StrictMode double-mounting on slow
// mobile networks (intermittent "Failed to fetch" / aborted requests).
const _glbBufCache = new Map(); // path → Promise<ArrayBuffer>
const fetchGlbBuffer = (path) => {
  if (!_glbBufCache.has(path)) {
    const p = fetch(path).then((r) => {
      if (!r.ok) throw new Error(`GLB HTTP ${r.status} ${r.statusText}`);
      return r.arrayBuffer();
    });
    p.catch((e) => {
      // eslint-disable-next-line no-console
      console.error("[GLB] fetch failed:", path, e);
      _glbBufCache.delete(path);
    });
    _glbBufCache.set(path, p);
  }
  return _glbBufCache.get(path);
};

const loadGlbCached = async (path) => {
  const [buf, { GLTFLoader }] = await Promise.all([
    fetchGlbBuffer(path),
    import("three/examples/jsm/loaders/GLTFLoader.js"),
  ]);
  return new Promise((resolve, reject) => {
    new GLTFLoader().parse(buf, "", resolve, reject);
  });
};

const SURCHARGE = 15.0;

// Palette for inline styles — matches Shadow Market shop aesthetic.
// Primary accent is ORANGE (#f5a623), not cyan. Add-to-cart keeps cyan.
const C = {
  bg: "#07080f",
  bg2: "#0a0b14",
  bdr: "rgba(255,255,255,0.08)",
  bdrSoft: "rgba(255,255,255,0.06)",
  cyan: "#00d4ff",
  mut: "rgba(255,255,255,0.4)",
  mutSoft: "rgba(255,255,255,0.35)",
  wht: "#ffffff",
  text: "#e0e0e0",
  amb: "#f5a623",
  grn: "#00e676",
  red: "#ff3355",
};

export const Studio3DClient = forwardRef(function Studio3DClient(
  { product },
  ref
) {
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const { isPro } = useAuth();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);

  // Refs to Three.js objects
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const rafRef = useRef(0);
  const clockRef = useRef(null);
  const mouseRef = useRef({ dn: false, x: 0, y: 0, tX: 0, tY: 0, rX: 0, rY: 0 });
  const autoRotRef = useRef(true);
  const compositeCanvasRef = useRef(null);
  const compositeTexRef = useRef(null);
  const threeRef = useRef(null); // cached THREE module
  const applyRef = useRef(null); // stable ref to latest applyColorAndDesign

  // UI state
  const [colorIdx, setColorIdx] = useState(0);
  const [designImg, setDesignImg] = useState(null);
  const [customText, setCustomText] = useState("");
  const [textColor, setTextColor] = useState("#00d4ff");
  const [selectedFont, setSelectedFont] = useState("Bebas Neue");
  const [designScale, setDesignScale] = useState(100);
  const [designPosX, setDesignPosX] = useState(0);
  const [designPosY, setDesignPosY] = useState(0);
  const [designSide, setDesignSide] = useState("front"); // 'front' | 'back'
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState("M");
  const [tab, setTab] = useState("diseno");
  // Accordion state — controls which section of the side panel is expanded.
  // 'color' | 'size' | 'diseno' | 'texto' | 'ajuste' | null
  const [accOpen, setAccOpen] = useState("color");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [added, setAdded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false); // mobile bottom-sheet

  const basePrice = isPro ? product.proPrice : product.price;
  const totalPrice = +(basePrice + SURCHARGE).toFixed(2);

  // Expose imperative API
  useImperativeHandle(
    ref,
    () => ({
      captureSnapshot: () => {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        if (!renderer || !scene || !camera) return null;
        renderer.render(scene, camera);
        try {
          return renderer.domElement.toDataURL("image/jpeg", 0.75);
        } catch (e) {
          return null;
        }
      },
    }),
    []
  );

  // ── Unified texture builder: paints garment color + user design
  // onto a single 1024×1024 composite canvas and applies it as the
  // diffuse map of every mesh in the GLB model. The model's own UVs
  // do the rest — the print appears printed on the chest.
  const applyColorAndDesign = useCallback(() => {
    const canvas = compositeCanvasRef.current;
    const tex = compositeTexRef.current;
    const THREE = threeRef.current;
    if (!canvas || !tex || !THREE) return;

    const ctx = canvas.getContext("2d");
    const W = 1024;
    const H = 1024;
    const col = COLORS[colorIdx];

    // 1) Garment base color background
    ctx.fillStyle = col.hex;
    ctx.fillRect(0, 0, W, H);

    // Subtle fabric grain
    for (let i = 0; i < 3000; i += 1) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.018})`;
      ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
    }

    // Soft vignette to suggest fabric shading
    const vg = ctx.createRadialGradient(W / 2, H / 2, W * 0.05, W / 2, H / 2, W * 0.75);
    vg.addColorStop(0, "rgba(255,255,255,0.04)");
    vg.addColorStop(0.7, "rgba(0,0,0,0.0)");
    vg.addColorStop(1, "rgba(0,0,0,0.25)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);

    // 2) Design placement on the chest area.
    // The polo/tshirt GLB UVs split the front and back panels in opposite
    // halves of the canvas. We expose a designSide selector and use the
    // matching UV coordinates. The UV island is mirrored on X for the front,
    // so we flip the design horizontally before drawing it on the front side.
    const CHEST_FRONT_CX = W * 0.25;
    const CHEST_FRONT_CY = H * 0.70;
    // Back panel is usually placed in the opposite quadrant of the UV atlas.
    // For the supplied polo/tshirt GLBs that means around (0.75, 0.70).
    const CHEST_BACK_CX = W * 0.75;
    const CHEST_BACK_CY = H * 0.70;
    const isFront = designSide === "front";
    const chestCX =
      (isFront ? CHEST_FRONT_CX : CHEST_BACK_CX) + designPosX * 4;
    const chestCY =
      (isFront ? CHEST_FRONT_CY : CHEST_BACK_CY) + designPosY * 4;
    const designSz = W * 0.18 * (designScale / 100);

    const finalize = () => {
      if (modelRef.current) {
        modelRef.current.traverse((child) => {
          if (!child.isMesh) return;
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          const updated = mats.map((m) => {
            const nm = m.clone ? m.clone() : m;
            if (nm.color) nm.color.set(0xffffff); // white so the canvas colors come through
            nm.map = compositeTexRef.current;
            if (nm.map) nm.map.colorSpace = THREE.SRGBColorSpace;
            nm.roughnessMap = null;
            nm.metalnessMap = null;
            nm.aoMap = null;
            nm.emissiveMap = null;
            nm.roughness = 0.9;
            nm.metalness = 0.0;
            if (nm.emissive) nm.emissive = new THREE.Color(0x000000);
            nm.transparent = false;
            nm.opacity = 1.0;
            nm.side = THREE.DoubleSide;
            nm.needsUpdate = true;
            return nm;
          });
          child.material = Array.isArray(child.material) ? updated : updated[0];
        });
      }
      compositeTexRef.current.needsUpdate = true;
    };

    // Helper: draw the user's text at the current canvas origin using the
    // currently selected webfont. Pass `extraOffsetY` to position relative
    // to a previously drawn image (chest area).
    const drawText = (size, extraOffsetY = 0) => {
      if (!customText) return;
      ctx.fillStyle = textColor;
      ctx.font = `bold ${size}px "${selectedFont}", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(customText.toUpperCase(), 0, extraOffsetY);
    };

    // Kick off the webfont preload (Promise resolves immediately if cached).
    const fontReady = ensureFontLoaded(selectedFont);

    if (designImg) {
      const img = new Image();
      img.onload = async () => {
        await fontReady;
        ctx.save();
        ctx.translate(chestCX, chestCY);
        // Both UV islands (front and back) on the supplied GLBs are mirrored
        // on the X axis, so we flip the design horizontally before drawing.
        ctx.scale(-1, 1);
        ctx.drawImage(img, -designSz / 2, -designSz / 2, designSz, designSz);
        const txtPx = getFontPxFor(selectedFont) * 0.75 * (designScale / 100);
        drawText(txtPx, designSz / 2 + txtPx + 6);
        ctx.restore();
        finalize();
      };
      img.onerror = () => finalize();
      img.src = designImg;
      return;
    }

    if (customText) {
      fontReady.then(() => {
        ctx.save();
        ctx.translate(chestCX, chestCY);
        ctx.scale(-1, 1);
        const txtPx = getFontPxFor(selectedFont) * (designScale / 100);
        drawText(txtPx, 0);
        ctx.restore();
        finalize();
      });
      return;
    }

    finalize();
  }, [colorIdx, designImg, customText, textColor, selectedFont, designScale, designPosX, designPosY, designSide]);

  // ── Boot Three.js + load model ────────────────────────
  // IMPORTANT: this effect depends ONLY on product.type. Previously it also
  // depended on `applyColorAndDesign` which recreates on every UI state
  // change, causing the effect to teardown Three.js mid-load on mobile and
  // abort the GLB fetch ("Failed to fetch"). We now route through `applyRef`.
  useEffect(() => {
    applyRef.current = applyColorAndDesign;
  }, [applyColorAndDesign]);

  useEffect(() => {
    let cancelled = false;
    let ro = null;

    const boot = async () => {
      const el = canvasRef.current;
      if (!el) return;

      const THREE = await import("three");
      threeRef.current = THREE;

      if (cancelled) return;

      // Create composite canvas + texture BEFORE loading the model so
      // applyColorAndDesign() can attach it as soon as the mesh appears.
      const compositeCanvas = document.createElement("canvas");
      compositeCanvas.width = 1024;
      compositeCanvas.height = 1024;
      compositeCanvasRef.current = compositeCanvas;
      const compositeTex = new THREE.CanvasTexture(compositeCanvas);
      compositeTex.colorSpace = THREE.SRGBColorSpace;
      compositeTex.anisotropy = 8;
      compositeTex.flipY = false; // GLB textures are typically not flipped
      compositeTexRef.current = compositeTex;

      // Wait for container size
      let safety = 0;
      while (el.offsetWidth < 50 && safety < 30) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 100));
        safety += 1;
      }
      if (cancelled) return;

      const W = el.offsetWidth || 800;
      const H = el.offsetHeight || 600;

      // Renderer
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(W, H);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.8;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      Object.assign(renderer.domElement.style, {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        display: "block",
      });
      el.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // Scene
      const scene = new THREE.Scene();

      // Studio-photo style background: radial gradient (light center → dark edges).
      // Makes dark garments readable instead of disappearing into pure black.
      const bgCanvas = document.createElement("canvas");
      bgCanvas.width = 1024;
      bgCanvas.height = 1024;
      const bgCtx = bgCanvas.getContext("2d");
      const grad = bgCtx.createRadialGradient(512, 420, 0, 512, 420, 600);
      grad.addColorStop(0,    "#3a3d52");
      grad.addColorStop(0.45, "#1e2030");
      grad.addColorStop(0.75, "#0e0f1a");
      grad.addColorStop(1,    "#07080f");
      bgCtx.fillStyle = grad;
      bgCtx.fillRect(0, 0, 1024, 1024);
      const bgTex = new THREE.CanvasTexture(bgCanvas);
      bgTex.colorSpace = THREE.SRGBColorSpace;
      scene.background = bgTex;
      sceneRef.current = scene;

      // Camera
      const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 100);
      camera.position.set(0, 1.2, 6);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      // Lights — moderate so black stays black, cream stays cream.
      // Slight bump from previous values so the model reads clearly on
      // mobile screens (dimmer brightness) without blowing out cream/gray.
      const ambient = new THREE.AmbientLight(0xffffff, 0.55);
      scene.add(ambient);

      const front = new THREE.DirectionalLight(0xffffff, 1.4);
      front.position.set(0, 2, 8);
      front.castShadow = true;
      front.shadow.mapSize.width = 2048;
      front.shadow.mapSize.height = 2048;
      scene.add(front);

      const side = new THREE.DirectionalLight(0xffffff, 0.6);
      side.position.set(-5, 2, 3);
      scene.add(side);

      // Frontal spotlight — illuminates the front face of dark garments
      const frontSpot = new THREE.SpotLight(0xffffff, 4.5);
      frontSpot.position.set(0, 3, 8);
      frontSpot.target.position.set(0, 0, 0);
      frontSpot.angle = Math.PI / 4;
      frontSpot.penumbra = 0.3;
      frontSpot.decay = 1.5;
      scene.add(frontSpot);
      scene.add(frontSpot.target);

      // Top fill so shoulders read properly
      const topFill = new THREE.DirectionalLight(0xffffff, 1.4);
      topFill.position.set(0, 8, 2);
      scene.add(topFill);

      // Shadow floor (catches shadows from the directional light)
      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(20, 20),
        new THREE.ShadowMaterial({ opacity: 0.2 })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = -2.2;
      floor.receiveShadow = true;
      scene.add(floor);

      // Soft studio floor plate (radial fade)
      const floorCanvas = document.createElement("canvas");
      floorCanvas.width = 512;
      floorCanvas.height = 512;
      const floorCtx = floorCanvas.getContext("2d");
      const floorGrad = floorCtx.createRadialGradient(256, 256, 0, 256, 256, 256);
      floorGrad.addColorStop(0,   "rgba(80,85,110,0.6)");
      floorGrad.addColorStop(0.5, "rgba(30,32,48,0.4)");
      floorGrad.addColorStop(1,   "rgba(7,8,15,0)");
      floorCtx.fillStyle = floorGrad;
      floorCtx.fillRect(0, 0, 512, 512);
      const floorPlate = new THREE.Mesh(
        new THREE.PlaneGeometry(8, 8),
        new THREE.MeshBasicMaterial({
          map: new THREE.CanvasTexture(floorCanvas),
          transparent: true,
          depthWrite: false,
        })
      );
      floorPlate.rotation.x = -Math.PI / 2;
      floorPlate.position.y = -2.3;
      scene.add(floorPlate);

      // Load GLB via cached ArrayBuffer + GLTFLoader.parse(). We use
      // SkeletonUtils.clone() because GLB models may be rigged
      // (SkinnedMesh + Skeleton); a plain Object3D.clone() breaks bone
      // references → model collapses or hides.
      const modelPath = MODEL_PATH[product.type] || MODEL_PATH.camiseta;
      Promise.all([
        loadGlbCached(modelPath),
        import("three/examples/jsm/utils/SkeletonUtils.js"),
      ])
        .then(([gltf, SkeletonUtils]) => {
          if (cancelled) return;
          const sourceModel = SkeletonUtils.clone(gltf.scene);

          // Step 1: collect every mesh and bake its world matrix into the
          // geometry. We then detach each mesh from its GLB parent chain
          // (which can carry intrinsic rotations) and re-parent it onto a
          // single, clean Group whose transform we own end-to-end.
          const flat = new THREE.Group();
          const meshesToFlatten = [];
          sourceModel.traverse((child) => {
            if (child.isMesh && child.geometry) meshesToFlatten.push(child);
          });
          meshesToFlatten.forEach((mesh) => {
            mesh.updateMatrixWorld(true);
            mesh.geometry = mesh.geometry.clone();
            mesh.geometry.applyMatrix4(mesh.matrixWorld);
            mesh.position.set(0, 0, 0);
            mesh.rotation.set(0, 0, 0);
            mesh.scale.set(1, 1, 1);
            mesh.updateMatrix();
            if (mesh.parent) mesh.parent.remove(mesh);
            flat.add(mesh);
          });

          // Step 2: figure out if the model is "lying down". A standing
          // shirt has the largest extent on the Y (height) axis. If Y is
          // the SMALLEST extent, the model is laid down — apply a -90° X
          // rotation to stand it up. We measure on the flattened geometry
          // BEFORE applying the corrective rotation.
          const rawBox = new THREE.Box3().setFromObject(flat);
          const rawSize = rawBox.getSize(new THREE.Vector3());
          if (rawSize.y < rawSize.x * 0.6 && rawSize.y < rawSize.z * 0.6) {
            const liftMatrix = new THREE.Matrix4().makeRotationX(-Math.PI / 2);
            flat.children.forEach((mesh) => {
              if (mesh.geometry) mesh.geometry.applyMatrix4(liftMatrix);
            });
          }

          // Step 3: center the flattened geometry around (0,0,0).
          const preBox = new THREE.Box3().setFromObject(flat);
          const preCenter = new THREE.Vector3();
          preBox.getCenter(preCenter);
          flat.children.forEach((mesh) => {
            if (mesh.geometry) {
              mesh.geometry.translate(-preCenter.x, -preCenter.y, -preCenter.z);
            }
          });

          // Step 4: pick a target world size and scale.
          const box = new THREE.Box3().setFromObject(flat);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          const scale = 3.5 / maxDim;

          // Wrap in an outer Group. The animation loop manipulates this
          // wrapper; `flat` is just its centered child.
          const model = new THREE.Group();
          flat.scale.setScalar(scale);
          // GLB t-shirt models have the front face pointing along +Z (away
          // from camera). Rotate the inner flat group 180° on Y so the
          // front faces the camera. We apply this on the INNER group
          // because the outer wrapper's rotation.y is driven by the
          // animation loop and would be overwritten.
          flat.rotation.y = Math.PI;
          model.add(flat);

          model.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
              child.frustumCulled = false; // safer for skinned models
              // Replace material with MeshStandardMaterial (basic GLB
              // models sometimes come without IBL and render fully dark).
              // We give it a neutral grey base — applyColorAndDesign will
              // immediately overwrite the color/map once the canvas
              // composite is ready.
              child.material = new THREE.MeshStandardMaterial({
                color: 0xcccccc,
                roughness: 0.85,
                metalness: 0.0,
                side: THREE.DoubleSide,
              });
              child.userData.baseMaterial = child.material.clone();
            }
          });

          scene.add(model);
          modelRef.current = model;

          // Reset auto-rotate / drag state so the initial frame starts
          // squarely on the front view.
          mouseRef.current.tY = 0;
          mouseRef.current.rY = 0;
          mouseRef.current.tX = 0;
          mouseRef.current.rX = 0;

          // Frontal camera position.
          if (cameraRef.current) {
            cameraRef.current.position.set(0, 0.5, 7);
            cameraRef.current.lookAt(0, 0, 0);
          }

          setLoading(false);
          // Paint composite texture + assign as map for every mesh
          setTimeout(() => applyRef.current?.(), 80);
        })
        .catch((err) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error("Error loading GLB:", err);
          setLoading(false);
          setLoadError(true);
        });

      // Clock
      clockRef.current = new THREE.Clock();

      // Animation loop
      const animate = () => {
        rafRef.current = requestAnimationFrame(animate);
        const tt = clockRef.current.getElapsedTime();
        const m = mouseRef.current;
        m.rX += (m.tX - m.rX) * 0.07;
        m.rY += (m.tY - m.rY) * 0.07;
        if (modelRef.current) {
          modelRef.current.rotation.x = m.rX * 0.5;
          modelRef.current.rotation.y = autoRotRef.current ? tt * 0.3 : m.rY;
          modelRef.current.position.y = Math.sin(tt * 0.6) * 0.06;
        }
        renderer.render(scene, camera);
      };
      animate();

      // Resize observer
      ro = new ResizeObserver(() => {
        const nW = el.offsetWidth;
        const nH = el.offsetHeight;
        if (nW > 10 && nH > 10) {
          renderer.setSize(nW, nH);
          camera.aspect = nW / nH;
          camera.updateProjectionMatrix();
        }
      });
      ro.observe(el);
    };

    boot();

    return () => {
      cancelled = true;
      ro?.disconnect();
      cancelAnimationFrame(rafRef.current);
      if (rendererRef.current) {
        const cv = rendererRef.current.domElement;
        if (canvasRef.current?.contains(cv)) canvasRef.current.removeChild(cv);
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
      if (sceneRef.current) {
        sceneRef.current.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose?.();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
            else obj.material.dispose?.();
          }
        });
      }
      modelRef.current = null;
      compositeCanvasRef.current = null;
      compositeTexRef.current?.dispose?.();
      compositeTexRef.current = null;
    };
  }, [product.type]);

  // ── Re-apply texture whenever color/design/text/scale/position change ──
  useEffect(() => {
    if (!modelRef.current || !threeRef.current) return;
    const id = setTimeout(applyColorAndDesign, 100);
    return () => clearTimeout(id);
  }, [applyColorAndDesign]);

  // ── Mouse / Touch / Wheel ──
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const m = mouseRef.current;
    let pinchDist = 0;
    const dn = (e) => {
      // Two-finger touch → pinch zoom, NOT drag
      if (e.touches && e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchDist = Math.hypot(dx, dy);
        m.dn = false;
        return;
      }
      m.dn = true;
      m.x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      m.y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      autoRotRef.current = false;
    };
    const mv = (e) => {
      // Two-finger pinch → adjust camera dolly
      if (e.touches && e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const d = Math.hypot(dx, dy);
        if (pinchDist > 0 && cameraRef.current) {
          cameraRef.current.position.z = Math.max(
            3,
            Math.min(14, cameraRef.current.position.z - (d - pinchDist) * 0.025)
          );
        }
        pinchDist = d;
        return;
      }
      if (!m.dn) return;
      const cx = e.clientX ?? e.touches?.[0]?.clientX ?? m.x;
      const cy = e.clientY ?? e.touches?.[0]?.clientY ?? m.y;
      m.tY += (cx - m.x) * 0.01;
      m.tX += (cy - m.y) * 0.008;
      m.tX = Math.max(-0.5, Math.min(0.5, m.tX));
      m.x = cx;
      m.y = cy;
    };
    const up = () => {
      m.dn = false;
      pinchDist = 0;
    };
    const wh = (e) => {
      if (cameraRef.current) {
        cameraRef.current.position.z = Math.max(
          3,
          Math.min(14, cameraRef.current.position.z + e.deltaY * 0.012)
        );
      }
    };
    el.addEventListener("mousedown", dn);
    el.addEventListener("mousemove", mv);
    el.addEventListener("mouseup", up);
    el.addEventListener("mouseleave", up);
    el.addEventListener("touchstart", dn, { passive: true });
    el.addEventListener("touchmove", mv, { passive: true });
    el.addEventListener("touchend", up);
    el.addEventListener("wheel", wh, { passive: true });
    return () => {
      el.removeEventListener("mousedown", dn);
      el.removeEventListener("mousemove", mv);
      el.removeEventListener("mouseup", up);
      el.removeEventListener("mouseleave", up);
      el.removeEventListener("touchstart", dn);
      el.removeEventListener("touchmove", mv);
      el.removeEventListener("touchend", up);
      el.removeEventListener("wheel", wh);
    };
  }, []);

  const setView = (v) => {
    autoRotRef.current = false;
    const m = mouseRef.current;
    m.tX = 0;
    m.tY = v === "front" ? 0 : v === "back" ? Math.PI : Math.PI / 2;
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      alert("Imagen demasiado grande (máx 5 MB)");
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => setDesignImg(ev.target?.result);
    r.readAsDataURL(f);
  };

  const captureSnapshot = () => {
    const r = rendererRef.current;
    const s = sceneRef.current;
    const c = cameraRef.current;
    if (!r || !s || !c) return null;
    r.render(s, c);
    try {
      return r.domElement.toDataURL("image/jpeg", 0.7);
    } catch (e) {
      return null;
    }
  };

  const handleAddToCart = () => {
    const preview = captureSnapshot();
    const colorChoice = COLORS[colorIdx];
    addItem(
      product,
      selectedSize,
      1,
      {
        baseColor: colorChoice.hex,
        baseColorLabel: colorChoice.name,
        text: customText.trim() || null,
        textColor,
        textFont: customText.trim() ? selectedFont : null,
        imageDataUrl: designImg,
        previewDataUrl: preview,
        designScale,
        designPosX,
        designPosY,
        designSide,
        surcharge: SURCHARGE,
      }
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
    setTimeout(() => openCart(), 280);
  };

  const tabSt = (active) => ({
    flex: 1,
    background: active ? "rgba(245,166,35,0.08)" : "transparent",
    border: `1px solid ${active ? "rgba(245,166,35,0.3)" : C.bdr}`,
    color: active ? C.amb : C.mutSoft,
    padding: "6px 4px",
    fontFamily: "'Courier New', monospace",
    fontSize: "0.65rem",
    letterSpacing: "0.1em",
    borderRadius: 6,
    cursor: "pointer",
    transition: "all .2s",
    textTransform: "uppercase",
  });

  // Accordion: collapsible section header (clickable). Only one section can be
  // open at a time; clicking the open one collapses it.
  const accSection = (id, emoji, label, summary) => {
    const isOpen = accOpen === id;
    return (
      <button
        type="button"
        onClick={() => setAccOpen((cur) => (cur === id ? null : id))}
        data-testid={`studio-acc-toggle-${id}`}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "11px 14px",
          cursor: "pointer",
          color: isOpen ? C.amb : "rgba(255,255,255,0.7)",
          fontSize: 11,
          letterSpacing: "0.1em",
          background: isOpen ? "rgba(245,166,35,0.06)" : "transparent",
          border: "none",
          borderTop: `1px solid ${C.bdr}`,
          fontFamily: "inherit",
          textAlign: "left",
          transition: "background .18s, color .18s",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
          <span aria-hidden="true">{emoji}</span>
          <span>{label}</span>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          {summary != null && summary !== "" && (
            <span
              data-testid={`studio-acc-summary-${id}`}
              style={{
                color: C.amb,
                fontSize: 9,
                letterSpacing: "0.06em",
                opacity: isOpen ? 0.55 : 0.9,
                maxWidth: 140,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {summary}
            </span>
          )}
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
            {isOpen ? "▾" : "▸"}
          </span>
        </span>
      </button>
    );
  };

  return (
    <div
      className="studio-root"
      data-testid="studio-3d-root"
    >
      {/* ── CANVAS 3D ── */}
      <div
        ref={canvasRef}
        className="studio-canvas"
        style={{
          position: "relative",
          overflow: "hidden",
          cursor: "grab",
          minWidth: 0,
          background: C.bg,
        }}
        data-testid="studio-3d-viewport"
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 12,
              zIndex: 20,
              background: C.bg,
            }}
            data-testid="studio-3d-loader"
          >
            <div
              style={{
                fontSize: ".7rem",
                letterSpacing: ".2em",
                color: C.cyan,
              }}
            >
              {t.customize_loading || "CARGANDO MODELO 3D"}
            </div>
            <div
              style={{
                width: 180,
                height: 2,
                background: C.bdr,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  background: C.cyan,
                  animation: "loadBar 1.5s ease infinite",
                  borderRadius: 2,
                }}
              />
            </div>
            <style>{`@keyframes loadBar { 0%{width:0%} 50%{width:100%} 100%{width:0%} }`}</style>
          </div>
        )}

        {loadError && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: C.red,
              fontSize: ".75rem",
              letterSpacing: ".18em",
              zIndex: 20,
            }}
            data-testid="studio-3d-error"
          >
            Error cargando modelo 3D
          </div>
        )}

        {!loading && !loadError && (
          <div
            className="studio-view-bar"
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 6,
              zIndex: 10,
            }}
          >
            {["front", "side", "back"].map((v) => (
              <button
                key={v}
                className="studio-view-btn"
                onClick={() => setView(v)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                  e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                }}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.5)",
                  padding: "6px 14px",
                  fontFamily: "'Courier New', monospace",
                  fontSize: "0.62rem",
                  letterSpacing: "0.12em",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backdropFilter: "blur(8px)",
                  textTransform: "uppercase",
                }}
                data-testid={`studio-view-${v}`}
              >
                {v === "front" ? "FRENTE" : v === "side" ? "LADO" : "ESPALDA"}
              </button>
            ))}
            <button
              className="studio-view-btn"
              onClick={() => {
                autoRotRef.current = true;
              }}
              style={{
                background: "rgba(245,166,35,0.12)",
                border: "1px solid rgba(245,166,35,0.4)",
                color: "#f5a623",
                padding: "6px 14px",
                fontFamily: "'Courier New', monospace",
                fontSize: "0.62rem",
                letterSpacing: "0.12em",
                borderRadius: 6,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              data-testid="studio-view-auto"
            >
              ↻ AUTO
            </button>
          </div>
        )}

        <div
          className="studio-mouse-hint"
          style={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(10,11,20,0.8)",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "5px 16px",
            borderRadius: 100,
            fontSize: "0.58rem",
            color: "rgba(255,255,255,0.35)",
            letterSpacing: "0.1em",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 10,
            fontFamily: "'Courier New', monospace",
          }}
        >
          🖱 Arrastra para rotar · Scroll para zoom
        </div>
        <div
          className="studio-touch-hint"
          style={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(10,11,20,0.8)",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "5px 16px",
            borderRadius: 100,
            display: "none",
            fontSize: "0.58rem",
            color: "rgba(255,255,255,0.5)",
            letterSpacing: "0.1em",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 10,
            fontFamily: "'Courier New', monospace",
          }}
        >
          👆 Arrastra · Pellizca para zoom
        </div>
      </div>

      {/* ── MOBILE FAB: open the bottom-sheet customization panel ── */}
      <button
        onClick={() => setSheetOpen((v) => !v)}
        className="studio-fab"
        data-testid="studio-mobile-toggle"
        aria-label={sheetOpen ? "Cerrar panel" : "Abrir panel"}
      >
        {sheetOpen ? "↓ CERRAR" : "↑ PERSONALIZAR"}
        <span style={{ color: C.amb, fontWeight: 900 }}> €{totalPrice.toFixed(2)}</span>
      </button>

      {/* ── SIDE PANEL ── */}
      <div
        className={`studio-panel ${sheetOpen ? "is-open" : ""}`}
        style={{
          background: C.bg2,
          borderLeft: `1px solid ${C.bdr}`,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
        data-testid="studio-side-panel"
      >
        <div
          style={{
            padding: "14px 14px 10px",
            borderBottom: `1px solid ${C.bdr}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: ".52rem",
              letterSpacing: ".18em",
              color: C.amb,
              marginBottom: 3,
            }}
          >
            PERSONALIZANDO
          </div>
          <div style={{ fontSize: ".85rem", fontWeight: 700, color: C.wht }}>
            {product.name}
          </div>
          <div style={{ fontSize: ".65rem", color: C.amb, marginTop: 2 }}>
            €{totalPrice.toFixed(2)}
            <span style={{ color: C.mut, fontSize: ".58rem" }}>
              {" "}
              (base €{basePrice.toFixed(2)} +€{SURCHARGE.toFixed(0)})
            </span>
          </div>
        </div>

        {/* Color — accordion section */}
        {accSection("color", "🎨", "COLOR DE LA PRENDA", COLORS[colorIdx].name)}
        {accOpen === "color" && (
          <div
            style={{
              padding: "12px 14px 14px",
              flexShrink: 0,
            }}
            data-testid="studio-acc-body-color"
          >
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {COLORS.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => setColorIdx(i)}
                  title={c.name}
                  aria-label={c.name}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: c.hex,
                    cursor: "pointer",
                    border: `2.5px solid ${colorIdx === i ? C.cyan : "transparent"}`,
                    boxShadow:
                      colorIdx === i ? "0 0 0 3px rgba(0,212,255,.2)" : "none",
                    transition: "all .2s",
                    outline: c.three > 0x808080 ? "1px solid #777" : "none",
                    padding: 0,
                  }}
                  data-testid={`studio-color-${i}`}
                />
              ))}
            </div>
            <div style={{ fontSize: ".58rem", color: C.mut, marginTop: 6 }}>
              {COLORS[colorIdx].name}
            </div>
          </div>
        )}

        {/* Size — accordion section */}
        {accSection("size", "📐", "TALLA", selectedSize)}
        {accOpen === "size" && (
          <div
            style={{
              padding: "12px 14px 14px",
              flexShrink: 0,
            }}
            data-testid="studio-acc-body-size"
          >
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {["XS", "S", "M", "L", "XL", "XXL"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSize(s)}
                  style={{
                    background: selectedSize === s ? "rgba(245,166,35,0.1)" : "transparent",
                    border: `1px solid ${selectedSize === s ? "rgba(245,166,35,0.5)" : "rgba(255,255,255,0.1)"}`,
                    color: selectedSize === s ? C.amb : C.mut,
                    padding: "5px 10px",
                    fontFamily: "'Courier New', monospace",
                    fontSize: "0.7rem",
                    letterSpacing: "0.05em",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "all .2s",
                  }}
                  data-testid={`studio-size-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tabs nav — removed: now controlled by accordion accOpen state. */}

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 0 }}>
          {/* Diseño — accordion section */}
          {accSection(
            "diseno",
            "🖼",
            "DISEÑO",
            selectedDesignId
              ? (REBEL_DESIGNS.find((d) => d.id === selectedDesignId)?.name || "personalizado")
              : designImg
                ? "personalizado"
                : ""
          )}
          {accOpen === "diseno" && (
            <div
              style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}
              data-testid="studio-acc-body-diseno"
            >
              {/* Position selector: front / back */}
              <div>
                <div
                  style={{
                    fontSize: "0.52rem",
                    letterSpacing: "0.18em",
                    color: C.mut,
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  POSICIÓN EN LA PRENDA
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { k: "front", label: "↑ FRENTE", view: "front" },
                    { k: "back", label: "↓ ESPALDA", view: "back" },
                  ].map((s) => {
                    const active = designSide === s.k;
                    return (
                      <button
                        key={s.k}
                        onClick={() => {
                          setDesignSide(s.k);
                          // auto-rotate the model so the user sees where the design lands
                          setView(s.view);
                        }}
                        style={{
                          flex: 1,
                          background: active
                            ? "rgba(245,166,35,0.1)"
                            : "transparent",
                          border: `1px solid ${
                            active ? "rgba(245,166,35,0.5)" : "rgba(255,255,255,0.1)"
                          }`,
                          color: active ? C.amb : C.mut,
                          padding: "7px 10px",
                          fontFamily: "'Courier New', monospace",
                          fontSize: "0.65rem",
                          letterSpacing: "0.1em",
                          borderRadius: 6,
                          cursor: "pointer",
                          transition: "all .2s",
                          textTransform: "uppercase",
                        }}
                        data-testid={`studio-side-${s.k}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── RE₿EL EXCLUSIVE DESIGN — compact panel control (opens modal) ── */}
              {(() => {
                const DT = getStudioDesignsI18n(lang);
                const sel = selectedDesignId
                  ? REBEL_DESIGNS.find((d) => d.id === selectedDesignId)
                  : null;
                return (
                  <div data-testid="studio-rebel-gallery">
                    {sel ? (
                      // Selected design summary card
                      <div
                        data-testid="studio-rebel-selected-card"
                        style={{
                          background: "rgba(0,212,255,.06)",
                          border: "1px solid rgba(0,212,255,.25)",
                          borderRadius: 8,
                          padding: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            background: "#000",
                            borderRadius: 5,
                            overflow: "hidden",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundImage:
                              "repeating-conic-gradient(#111 0% 25%, #1a1a1a 0% 50%)",
                            backgroundSize: "10px 10px",
                          }}
                        >
                          <img
                            src={sel.file}
                            alt=""
                            style={{ width: "90%", height: "90%", objectFit: "contain" }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: ".55rem",
                              color: "rgba(255,255,255,.35)",
                              marginBottom: 2,
                            }}
                          >
                            {DT.seleccionado}
                          </div>
                          <div
                            style={{
                              fontSize: ".7rem",
                              fontWeight: 600,
                              color: "#00d4ff",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sel.nombre[lang] || sel.nombre.es}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          <button
                            type="button"
                            onClick={() => setGalleryOpen(true)}
                            data-testid="studio-rebel-change"
                            style={{
                              background: "none",
                              border: "1px solid rgba(0,212,255,.3)",
                              color: "#00d4ff",
                              padding: "4px 8px",
                              fontFamily: "inherit",
                              fontSize: ".55rem",
                              letterSpacing: ".08em",
                              borderRadius: 3,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {DT.cambiar}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDesignImg(null);
                              setSelectedDesignId(null);
                            }}
                            data-testid="studio-rebel-remove"
                            style={{
                              background: "none",
                              border: "1px solid rgba(255,51,85,.25)",
                              color: "#ff3355",
                              padding: "4px 8px",
                              fontFamily: "inherit",
                              fontSize: ".55rem",
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            {DT.quitar}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Big "Open gallery" button with 4-thumbs preview
                      <button
                        type="button"
                        onClick={() => setGalleryOpen(true)}
                        data-testid="studio-rebel-open"
                        style={{
                          width: "100%",
                          background:
                            "linear-gradient(135deg, rgba(245,166,35,.08), rgba(0,212,255,.06))",
                          border: "1px solid rgba(245,166,35,.3)",
                          borderRadius: 8,
                          padding: "16px 12px",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 8,
                          transition: "all .2s",
                          fontFamily: "inherit",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 4,
                            width: 80,
                          }}
                        >
                          {REBEL_DESIGNS.slice(0, 4).map((d) => (
                            <div
                              key={d.id}
                              style={{
                                background: "#000",
                                borderRadius: 3,
                                height: 34,
                                backgroundImage:
                                  "repeating-conic-gradient(#111 0% 25%, #1a1a1a 0% 50%)",
                                backgroundSize: "8px 8px",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={d.file}
                                alt=""
                                style={{ width: "90%", height: "90%", objectFit: "contain" }}
                              />
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            color: "#f5a623",
                            fontSize: ".72rem",
                            fontWeight: 700,
                            letterSpacing: ".08em",
                          }}
                        >
                          {DT.abrirGaleria}
                        </div>
                        <div style={{ color: "rgba(255,255,255,.3)", fontSize: ".58rem" }}>
                          {REBEL_DESIGNS.length} {DT.countSuffix}
                        </div>
                      </button>
                    )}

                    {/* Separator */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 14,
                        marginBottom: 4,
                      }}
                    >
                      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.07)" }} />
                      <span
                        style={{
                          fontSize: ".52rem",
                          color: "rgba(255,255,255,.2)",
                          letterSpacing: ".1em",
                          whiteSpace: "nowrap",
                          textTransform: "uppercase",
                        }}
                      >
                        {DT.tuDiseno}
                      </span>
                      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.07)" }} />
                    </div>
                  </div>
                );
              })()}

              <label
                style={{
                  display: "block",
                  border: `2px dashed ${designImg ? "rgba(245,166,35,0.3)" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                  cursor: "pointer",
                  background: designImg ? "rgba(245,166,35,0.04)" : "rgba(255,255,255,0.02)",
                  transition: "all .25s",
                }}
                data-testid="studio-upload-label"
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  style={{ display: "none" }}
                  data-testid="studio-upload-input"
                />
                {designImg ? (
                  <>
                    <img
                      src={designImg}
                      alt=""
                      style={{
                        width: "100%",
                        height: 80,
                        objectFit: "contain",
                        borderRadius: 3,
                        marginBottom: 7,
                      }}
                    />
                    <div style={{ fontSize: ".62rem", color: C.amb }}>
                      ✓ Diseño cargado
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "1.8rem", marginBottom: 8 }}>⬆️</div>
                    <div
                      style={{
                        fontSize: ".65rem",
                        color: C.wht,
                        marginBottom: 4,
                      }}
                    >
                      Sube tu diseño
                    </div>
                    <div style={{ fontSize: ".58rem", color: C.mut }}>
                      PNG con fondo transparente
                    </div>
                  </>
                )}
              </label>
              {designImg && (
                <button
                  onClick={() => { setDesignImg(null); setSelectedDesignId(null); }}
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,51,85,.3)",
                    color: C.red,
                    padding: 6,
                    fontFamily: "inherit",
                    fontSize: ".6rem",
                    borderRadius: 3,
                    cursor: "pointer",
                  }}
                  data-testid="studio-clear-design"
                >
                  ✕ Quitar diseño
                </button>
              )}
              <div
                style={{
                  fontSize: ".6rem",
                  color: C.mut,
                  lineHeight: 1.6,
                  background: C.bg,
                  border: `1px solid ${C.bdr}`,
                  borderRadius: 4,
                  padding: 10,
                }}
              >
                💡 PNG con fondo transparente queda perfecto sobre cualquier color
                de prenda
              </div>
            </div>
          )}

          {/* Texto — accordion section */}
          {accSection(
            "texto",
            "✏️",
            "TEXTO PERSONALIZADO",
            customText ? `"${customText.slice(0, 14)}"` : ""
          )}
          {accOpen === "texto" && (
            <div
              style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}
              data-testid="studio-acc-body-texto"
            >
              <div>
                <div
                  style={{
                    fontSize: ".52rem",
                    letterSpacing: ".15em",
                    color: C.mut,
                    marginBottom: 6,
                  }}
                >
                  TEXTO
                </div>
                <input
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  maxLength={20}
                  placeholder="Escribe aquí..."
                  style={{
                    width: "100%",
                    background: C.bg,
                    border: `1px solid ${C.bdr}`,
                    color: C.wht,
                    padding: "7px 9px",
                    fontFamily: "inherit",
                    fontSize: ".7rem",
                    borderRadius: 3,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  data-testid="studio-text-input"
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: ".52rem",
                    letterSpacing: ".15em",
                    color: C.mut,
                    marginBottom: 6,
                  }}
                >
                  COLOR DEL TEXTO
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {["#00d4ff", "#f0f0e8", "#f5a623", "#ff3355", "#00e676", "#ffffff"].map(
                    (c) => (
                      <button
                        key={c}
                        onClick={() => setTextColor(c)}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: c,
                          cursor: "pointer",
                          border: `2px solid ${textColor === c ? "#fff" : "transparent"}`,
                          transition: "all .15s",
                          outline: "1px solid rgba(255,255,255,.1)",
                          padding: 0,
                        }}
                        data-testid={`studio-text-color-${c.replace("#", "")}`}
                        aria-label={c}
                      />
                    )
                  )}
                </div>
              </div>

              {/* ── Font selector (24 Google Fonts) ── */}
              <div>
                <div
                  style={{
                    fontSize: ".52rem",
                    letterSpacing: ".15em",
                    color: C.mut,
                    marginBottom: 6,
                  }}
                >
                  TIPOGRAFÍA
                </div>
                <FontSelector
                  selected={selectedFont}
                  onChange={setSelectedFont}
                  text={customText}
                />
              </div>
            </div>
          )}

          {/* Ajuste — accordion section */}
          {accSection(
            "ajuste",
            "⚙️",
            "AJUSTE Y TAMAÑO",
            `${designScale}%`
          )}
          {accOpen === "ajuste" && (
            <div
              style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}
              data-testid="studio-acc-body-ajuste"
            >
              {[
                {
                  l: "Tamaño",
                  v: designScale,
                  s: setDesignScale,
                  mn: 20,
                  mx: 200,
                  d: (v) => v + "%",
                  test: "studio-slider-scale",
                },
                {
                  l: "Posición ←→",
                  v: designPosX + 50,
                  s: (v) => setDesignPosX(v - 50),
                  mn: 0,
                  mx: 100,
                  d: (v) =>
                    v === 50 ? "Centro" : v > 50 ? `Der. +${v - 50}` : `Izq. ${50 - v}`,
                  test: "studio-slider-position-x",
                },
                {
                  l: "Posición ↑↓",
                  v: designPosY + 50,
                  s: (v) => setDesignPosY(v - 50),
                  mn: 0,
                  mx: 100,
                  d: (v) =>
                    v === 50 ? "Centro" : v > 50 ? `Abajo +${v - 50}` : `Arriba ${50 - v}`,
                  test: "studio-slider-position",
                },
              ].map((item) => (
                <div key={item.l}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: ".55rem",
                      color: C.mut,
                      marginBottom: 4,
                    }}
                  >
                    <span>{item.l}</span>
                    <span style={{ color: C.wht }}>{item.d(item.v)}</span>
                  </div>
                  <input
                    type="range"
                    min={item.mn}
                    max={item.mx}
                    value={item.v}
                    onChange={(e) => item.s(parseInt(e.target.value, 10))}
                    style={{ width: "100%", accentColor: C.cyan, height: 3 }}
                    data-testid={item.test}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA bottom */}
        <div
          style={{
            padding: 12,
            borderTop: `1px solid ${C.bdr}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: ".65rem",
              marginBottom: 10,
            }}
          >
            <span style={{ color: C.mut }}>Total</span>
            <span style={{ color: C.cyan, fontWeight: 700 }}>
              €{totalPrice.toFixed(2)}
            </span>
          </div>
          <button
            onClick={handleAddToCart}
            className="shop-btn-primary"
            style={{
              width: "100%",
              padding: "12px 22px",
              fontSize: "0.78rem",
              letterSpacing: "0.12em",
              marginBottom: 6,
              background: added
                ? "linear-gradient(90deg, #00e676 0%, #00b248 100%)"
                : undefined,
            }}
            data-testid="studio-add-to-cart"
          >
            {added ? "✓ AÑADIDO" : "AÑADIR AL CARRITO →"}
          </button>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: "100%",
              background: "none",
              border: `1px solid ${C.bdr}`,
              color: C.mut,
              padding: 7,
              fontFamily: "inherit",
              fontSize: ".62rem",
              borderRadius: 3,
              cursor: "pointer",
            }}
            data-testid="studio-back-btn"
          >
            ← VOLVER
          </button>
        </div>
      </div>

      {/* ── MODAL: Big RE₿EL Design gallery (additive overlay) ─────── */}
      {galleryOpen && (() => {
        const DT = getStudioDesignsI18n(lang);
        return (
          <div
            data-testid="studio-rebel-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setGalleryOpen(false); }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(7,8,15,.92)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
              animation: "studio-modal-fade .2s ease",
            }}
          >
            <div
              style={{
                background: "#0d0e1c",
                border: "1px solid #1c2240",
                borderRadius: 12,
                width: "100%",
                maxWidth: 860,
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                animation: "studio-modal-slide .25s ease",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "18px 22px",
                  borderBottom: "1px solid #1c2240",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                  gap: 16,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: ".95rem",
                      fontWeight: 700,
                      color: "#f0f4ff",
                      marginBottom: 3,
                    }}
                  >
                    {DT.tituloModal}
                  </div>
                  <div style={{ fontSize: ".62rem", color: "rgba(255,255,255,.3)" }}>
                    {DT.subModal}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGalleryOpen(false)}
                  data-testid="studio-rebel-modal-close"
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,255,255,.12)",
                    color: "rgba(255,255,255,.5)",
                    padding: "6px 14px",
                    fontFamily: "inherit",
                    fontSize: ".62rem",
                    letterSpacing: ".08em",
                    borderRadius: 5,
                    cursor: "pointer",
                    transition: "all .2s",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {DT.cerrar}
                </button>
              </div>

              {/* Grid */}
              <div
                style={{
                  padding: "20px 22px",
                  overflowY: "auto",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 16,
                }}
              >
                {REBEL_DESIGNS.map((d) => {
                  const isSel = selectedDesignId === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      data-testid={`studio-design-${d.id}`}
                      onClick={async () => {
                        try {
                          const res = await fetch(d.file);
                          const text = await res.text();
                          const blob = new Blob([text], { type: "image/svg+xml" });
                          const reader = new FileReader();
                          reader.onload = (ev) => setDesignImg(ev.target?.result);
                          reader.readAsDataURL(blob);
                          setSelectedDesignId(d.id);
                          setGalleryOpen(false);
                        } catch {
                          /* swallow — UX stays the same */
                        }
                      }}
                      style={{
                        background: isSel ? "rgba(0,212,255,.08)" : "rgba(255,255,255,.02)",
                        border: `1px solid ${isSel ? "#00d4ff" : "rgba(255,255,255,.08)"}`,
                        borderRadius: 8,
                        padding: 10,
                        cursor: "pointer",
                        transition: "all .18s",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        position: "relative",
                        textAlign: "left",
                        fontFamily: "inherit",
                      }}
                    >
                      <div
                        style={{
                          aspectRatio: "1 / 1",
                          background: "#000",
                          borderRadius: 6,
                          overflow: "hidden",
                          backgroundImage:
                            "repeating-conic-gradient(#111 0% 25%, #1a1a1a 0% 50%)",
                          backgroundSize: "16px 16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={d.file}
                          alt={d.nombre[lang] || d.nombre.es}
                          loading="lazy"
                          style={{ width: "85%", height: "85%", objectFit: "contain" }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: ".75rem",
                            fontWeight: 600,
                            color: isSel ? "#00d4ff" : "#f0f4ff",
                            marginBottom: 3,
                          }}
                        >
                          {d.nombre[lang] || d.nombre.es}
                        </div>
                        <div
                          style={{
                            fontSize: ".55rem",
                            letterSpacing: ".12em",
                            color: "rgba(255,255,255,.25)",
                          }}
                        >
                          {d.tag[lang] || d.tag.es}
                        </div>
                      </div>
                      {isSel && (
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: "#00d4ff",
                            color: "#07080f",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: ".75rem",
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
});

export default Studio3DClient;
